/**
 * dateUtils.ts - Date Handling and Formatting Utilities
 * 
 * This utility module provides consistent date handling functions to prevent timezone issues
 * and ensure uniform date formatting across the application.
 * 
 * Key Functions:
 * - dateInputToDate: Converts HTML date input values to Date objects safely
 * - dateToDateInput: Converts Date objects to HTML date input format
 * - formatDateForDisplay: Formats dates for user-friendly display
 * 
 * Features:
 * - Timezone-safe date conversions
 * - Consistent date input/output formatting
 * - Internationalization support for date display
 * - Null/undefined safety with fallbacks
 * 
 * Dependencies: None (uses native Date and Intl APIs)
 */

// Utility functions for consistent date handling across the application

/**
 * Converts a date input value (YYYY-MM-DD) to a Date object at local midnight
 * This prevents timezone issues when saving dates from date inputs
 */
export const dateInputToDate = (dateString: string): Date => {
  if (!dateString) return new Date();
  
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day); // month is 0-indexed
};

/**
 * Converts a Date object to a date input value (YYYY-MM-DD)
 * This ensures consistent formatting for date inputs
 */
export const dateToDateInput = (date: Date | string | null): string => {
  if (!date) return '';
  
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date for display in the details view
 * This ensures consistent formatting across the application
 */
export const formatDateForDisplay = (date: Date | string | null): string => {
  if (!date) return '';
  
  const d = new Date(date);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(d);
};